import { useEffect, useMemo, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { fetchZertifikate, fetchZertifikatsarten } from "@/lib/api"
import { SkeletonTabelle } from "@/components/Skeleton"
import { EmptyState } from "@/components/EmptyState"
import { formatDatum } from "@/lib/format"
import { Input, TableWrapper, TD_CLASS, TH_CLASS, TR_CLASS } from "@/components/ui"

const AMPEL_FARBEN: Record<string, string> = {
  gueltig: "bg-green-100 text-green-800",
  bald_ablaufend: "bg-orange-100 text-orange-800",
  abgelaufen: "bg-red-100 text-red-800",
}

const AMPEL_LABEL: Record<string, string> = {
  gueltig: "Gültig",
  bald_ablaufend: "Läuft bald ab",
  abgelaufen: "Abgelaufen",
}

/** Zertifikate-Übersicht mit Ablauf-Ampel — AP-2.6. */
export function ZertifikatePage() {
  useEffect(() => {
    document.title = "Zertifikate — RMNC Stammdaten"
  }, [])

  const [suche, setSuche] = useState("")

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["zertifikate"],
    queryFn: () => fetchZertifikate(),
  })

  const { data: arten } = useQuery({
    queryKey: ["zertifikatsarten"],
    queryFn: () => fetchZertifikatsarten(),
  })

  const artenById = useMemo(
    () => new Map((arten ?? []).map((a) => [a.id, a.name])),
    [arten],
  )

  const gefiltert = useMemo(() => {
    const q = suche.trim().toLocaleLowerCase("de")
    if (!q) return data ?? []
    return (data ?? []).filter((z) => {
      const art = z.zertifikatsartId ? artenById.get(z.zertifikatsartId) ?? "" : ""
      return art.toLocaleLowerCase("de").includes(q)
    })
  }, [data, suche, artenById])

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">Zertifikate</h1>
        <p className="text-sm text-slate-600">
          Ablauf-Tracking mit Ampel (Ninox `XE`/`YE`/`CF`) + täglicher Reminder-Lauf — AP-2.6.
        </p>
      </header>

      {(data?.length ?? 0) > 10 && (
        <Input
          type="text"
          placeholder="Suche nach Zertifikatsart…"
          value={suche}
          onChange={(e) => setSuche(e.target.value)}
          className="mb-4 w-64"
        />
      )}

      {isError && (
        <div className="mb-4 flex items-center justify-between rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>Zertifikate konnten nicht geladen werden.</span>
          <button
            type="button"
            onClick={() => refetch()}
            className="rounded-md font-medium underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
          >
            Erneut versuchen
          </button>
        </div>
      )}

      <TableWrapper>
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className={TH_CLASS}>Art</th>
              <th className={TH_CLASS}>Ablaufdatum</th>
              <th className={TH_CLASS}>Status</th>
            </tr>
          </thead>
          {isLoading ? (
            <SkeletonTabelle zeilen={4} spalten={3} />
          ) : (
            <tbody>
              {gefiltert.map((z) => (
                <tr key={z.id} className={TR_CLASS}>
                  <td className={`${TD_CLASS} text-slate-900`}>
                    {z.zertifikatsartId ? artenById.get(z.zertifikatsartId) ?? "—" : "—"}
                  </td>
                  <td className={TD_CLASS}>{formatDatum(z.aktuellesAblaufdatum)}</td>
                  <td className={TD_CLASS}>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        AMPEL_FARBEN[z.ampel ?? "gueltig"]
                      }`}
                    >
                      {AMPEL_LABEL[z.ampel ?? "gueltig"]}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </TableWrapper>

      {!isLoading && !isError && (data?.length ?? 0) === 0 && (
        <EmptyState text="Noch keine Zertifikate vorhanden." />
      )}
      {!isLoading && !isError && (data?.length ?? 0) > 0 && gefiltert.length === 0 && (
        <EmptyState text="Keine Zertifikate gefunden." />
      )}
    </div>
  )
}
